import { useState } from "react";
import { BodyScan } from "@/components/tools/TimedTool";
import { Ring, useClock } from "@/components/landing/CalmTry";
import { ToolCrisis, ToolInvite } from "@/components/tools/ToolFinish";
import { resetTrack, track } from "@/lib/toolTrack";

const Scale = ({ label, onPick, testPrefix }) => (
  <div className="text-center" data-testid={`${testPrefix}-scale`}>
    <p className="text-lg font-semibold">{label}</p>
    <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 mt-4 max-w-xl mx-auto">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button key={n} onClick={() => onPick(n)} className="rounded-2xl py-3 font-bold text-[#3A2E4F] transition-transform hover:scale-105" style={{ background: `hsl(${120 - n * 12} 70% ${88 - n * 2}%)` }} data-testid={`${testPrefix}-${n}`}>{n}</button>
      ))}
    </div>
    <div className="flex justify-between text-xs text-mm-muted mt-2 max-w-xl mx-auto"><span>Calm</span><span>Boiling</span></div>
  </div>
);

const Result = ({ before, after, tool, onAgain }) => {
  const drop = before - after;
  const msg = drop > 0 ? `Down ${drop} point${drop === 1 ? "" : "s"}, from ${before} to ${after}. Your body is cooling off.` : `Still at ${after}. That's okay, strong anger can take a few rounds. Try once more, or step away a little longer.`;
  return (
    <div className="mm-fade-up" data-testid="tool-anger-result">
      <p className="text-lg" data-testid="tool-anger-change">{msg}</p>
      <p className="text-sm text-mm-muted mt-2">{after >= 5 ? "Wait until you're below a 5 before replying or deciding anything." : "You're in a good place to choose your next step calmly."}</p>
      {after >= 8 && <ToolCrisis />}
      <ToolInvite tool={tool} onAgain={onAgain} card={{ line: "I cooled my anger down 🧊", sub: "Anger cool-down. Try it free.", emoji: "🧊" }} />
    </div>
  );
};

export const AngerTool = ({ tool }) => {
  const length = tool.length * 1000;
  const [stage, setStage] = useState("before");
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [elapsed, setElapsed] = useClock(stage === "run", () => setStage("after"), length);
  const begin = (n) => { resetTrack(tool.slug); setBefore(n); setElapsed(0); setStage("run"); track(tool.slug, "start"); };
  const again = () => { setBefore(null); setAfter(null); setStage("before"); };
  if (stage === "before") return <Scale label="How angry do you feel right now?" onPick={begin} testPrefix="tool-anger-before" />;
  if (stage === "after") return <Scale label="And now, how angry do you feel?" onPick={(n) => { setAfter(n); setStage("done"); track(tool.slug, "finish"); }} testPrefix="tool-anger-after" />;
  if (stage === "done") return <Result before={before} after={after} tool={tool} onAgain={again} />;
  return (
    <div className="grid sm:grid-cols-[1fr_auto] gap-6 items-center" data-testid="tool-anger-run">
      <BodyScan steps={tool.steps} elapsed={elapsed} length={length} />
      <div className="flex sm:flex-col items-center justify-center gap-3">
        <Ring elapsed={elapsed} length={length} />
        <button onClick={() => setStage("after")} className="mm-btn-ghost !px-4 !py-2 text-sm" data-testid="tool-anger-skip">I'm cooler now</button>
      </div>
    </div>
  );
};
